import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Employee, OvertimeWindow, Shift, ShiftCode } from "../lib/types";
import { adjacentShifts, candidateAssignmentDate } from "../lib/coverage";
import { hourOf, parseHourNote, bucketsBetween } from "../lib/hourBuckets";
import { isTacActiveOn, effectiveHomeShiftFor } from "../lib/tacMode";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { Avatar } from "./ui/Avatar";

// An OT window picked by the supervisor for a specific covering
// officer: either one of the home shift's presets, or a custom
// hours entry. Both paths end up stored as ot_start_time /
// ot_end_time on the assignment row.
interface OTHours {
  start: string;   // HH:MM
  end: string;     // HH:MM
  preset?: string; // label of the preset if one was picked
}

// Fix-coverage modal: opens from a red coverage / OIC cell on the
// Schedule or a red item on the Dashboard. Two flows —
//   * Full OT: one employee from a *different* home shift takes the
//     entire short shift as OT. Creates one assignment with note=OT
//     on the short shift.
//   * Split OT: one from the previous adjacent shift + one from the
//     next adjacent shift splits it half-and-half. Two OT½ rows.
// OIC-only gaps filter the candidate lists to OIC employees.
//
// The employee's existing home-shift assignment is left untouched —
// they work both shifts that day (stay late / come in early).
export function FixCoverageModal({
  shift, date, onClose,
}: {
  shift: Shift;
  date: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const codes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  // Agency-wide tac flag drives the same projection the Schedule
  // grid uses: when tac mode is on, every followsTacMode officer is
  // considered rostered to their tacHomeShiftId instead of their
  // raw homeShiftId. Without applying it here, "Fix coverage" on a
  // tac shift comes up empty (no one's raw home_shift_id points at
  // a tac shift) and the previous-shift / next-shift adjacency
  // lists pull from the wrong rosters.
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  // Department lookup so the Cross-department tab can show
  // "Dispatch", "Animal Control", etc. as the subtitle on each
  // candidate row — without it the picker reads as a flat name
  // list and the supervisor can't tell who's volunteering from
  // where.
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  // Fetch the surrounding window too: the prev-shift candidate's
  // qualifying home-shift assignment for a wrap-midnight shift
  // lives on (date-1), and the next-shift candidate's can live on
  // (date+1) when the next shift starts "before" the gap in wall-
  // clock time (e.g. Night gap → Day next starts tomorrow morning).
  const windowStart = shiftDate(date, -1);
  const windowEnd = shiftDate(date, +1);
  const assignments = useQuery({
    queryKey: ["assignments", windowStart, windowEnd],
    queryFn: () => api.listAssignments(windowStart, windowEnd),
  });
  const coverageQ = useQuery({
    queryKey: ["coverage", date, date],
    queryFn: () => api.coverage(date, date),
  });
  // Existing supervisor override for this (shift, date), if any.
  // Drives the toggle text + lets us reflect "marked covered"
  // immediately in the modal without re-fetching coverage.
  const overridesQ = useQuery({
    queryKey: ["coverageOverrides", date, date],
    queryFn: () => api.listCoverageOverrides(date, date),
  });
  const existingOverride = (overridesQ.data ?? []).find(
    (o) => o.shiftId === shift.id && o.date === date,
  );

  const [tab, setTab] = useState<"full" | "split" | "cross">("full");
  const [fullPick, setFullPick] = useState<string>("");
  const [prevPick, setPrevPick] = useState<string>("");
  const [nextPick, setNextPick] = useState<string>("");
  const [crossPick, setCrossPick] = useState<string>("");
  const [fullHours, setFullHours] = useState<OTHours | null>(null);
  const [prevHours, setPrevHours] = useState<OTHours | null>(null);
  const [nextHours, setNextHours] = useState<OTHours | null>(null);
  const [crossHours, setCrossHours] = useState<OTHours | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper: when an officer is picked, default their OT hours to
  // the actual gap on the shift being covered — the first
  // contiguous uncovered window from uncoveredWindows below. Falls
  // back to the officer's home-shift first preset when the shift
  // is fully covered (unusual for Fix Coverage but possible).
  // Supervisors can still switch presets or type custom hours.
  function defaultHoursFor(emp: Employee | undefined, currentShifts: Shift[]): OTHours | null {
    const gap = uncoveredWindows[0];
    if (gap) return { start: gap.start, end: gap.end };
    if (!emp?.homeShiftId) return null;
    const home = currentShifts.find((s) => s.id === emp.homeShiftId);
    const presets = home?.overtimeWindows ?? [];
    if (presets.length === 0) return null;
    const p = presets[0];
    return { start: p.startTime, end: p.endTime, preset: p.label };
  }

  const codeMap = useMemo(
    () => new Map((codes.data ?? []).map((c) => [c.code.toUpperCase(), c])),
    [codes.data],
  );

  const covRow = (coverageQ.data ?? []).find(
    (c) => c.shiftId === shift.id && c.date === date,
  );
  const totalShort = !!covRow && covRow.assigned < covRow.minStaffing;
  const oicShort = !!covRow && covRow.oicAssigned < covRow.oicRequired;
  const oicOnly = !totalShort && oicShort;

  // Per-date overlay: pick shifts and project employees as they
  // appear on THIS specific date. The Schedule grid uses a window-
  // wide overlay so it can render both shift families during a
  // straddling page; this modal is pinned to one cell so the
  // simpler per-date resolve gives the supervisor exactly the
  // candidates rostered to work that day.
  const tacOnDate = isTacActiveOn(agency.data, date);
  const visibleShifts = useMemo(
    () => (shifts.data ?? []).filter((s) =>
      tacOnDate ? s.tacRole !== "regular" : s.tacRole !== "tac"),
    [shifts.data, tacOnDate],
  );
  // IOD periods for the cell's date — officers on injury leave
  // shouldn't show up as OT candidates. Same date-range filter as
  // the schedule grid uses.
  const iodQ = useQuery({
    queryKey: ["iodPeriods", date, date],
    queryFn: () => api.listIodPeriods(date, date),
  });
  const iodForDate = useMemo(() => {
    const s = new Set<string>();
    for (const p of iodQ.data ?? []) {
      if (p.startDate > date) continue;
      if (p.endDate && p.endDate < date) continue;
      s.add(p.employeeId);
    }
    return s;
  }, [iodQ.data, date]);

  const projectedEmployees = useMemo(
    () => (employees.data ?? [])
      .filter((e) => {
        // Retired on/after this date or on IOD today → not in the
        // OT candidate pool. The schedule grid already gates them
        // out via effectiveHomeShiftFor + the IOD overlay; the
        // modal needs its own filter because it walks the raw
        // employees list.
        if (e.retirementDate && date >= e.retirementDate) return false;
        if (iodForDate.has(e.id)) return false;
        return true;
      })
      .map((e) => ({
        ...e,
        homeShiftId: effectiveHomeShiftFor(agency.data, e, date),
      })),
    [agency.data, employees.data, date, iodForDate],
  );

  const { prev, next } = useMemo(
    // Use the tac-filtered shift list so adjacency picks neighbours
    // from the SAME family the schedule is showing: tac shifts only
    // when tac mode is on, regular + exempt when it's off. Otherwise
    // a regular shift could land on a tac shift as its prev/next
    // (both share the department + inRotation gate) and the modal
    // would pull candidates from a roster that isn't visible.
    () => adjacentShifts(visibleShifts, shift.id),
    [visibleShifts, shift.id],
  );

  const dayAssignments = assignments.data ?? [];
  const homeRoster = projectedEmployees.filter(
    (e) => e.status !== "inactive" && e.homeShiftId === shift.id,
  );

  // Compute the CONTIGUOUS uncovered hour windows for this shift on
  // this date. Mirrors the api.ts coverage math (same bucketing,
  // same treatment of custom-hour notes) but stays client-side so
  // the modal can pre-fill the OT hours picker to the actual gap
  // without a round trip.
  //
  // Example: Day shift 06-14 requires 3 officers. Two are working
  // full shift, one has "6-12" in their cell. Hours 12-13 are short
  // → uncoveredWindows returns [{ start: "12:00", end: "14:00" }].
  //
  // Empty when the shift is fully covered (nothing to pre-fill).
  const uncoveredWindows = useMemo(() => {
    if (!covRow) return [];
    const shiftStartH = hourOf(shift.startTime);
    if (shiftStartH === null) return [];
    // Shift.endTime doesn't exist on the model — endHour is
    // derived from startTime + durationMin (both in the schema).
    // A 24h shift wraps back to its own start so bucketsBetween's
    // "start === end → []" guard would zero it out; bucket over
    // the full clock instead.
    const shiftEndH = (shiftStartH + Math.round(shift.durationMin / 60)) % 24;
    const shiftBuckets = shift.durationMin >= 24 * 60
      ? bucketsBetween(0, 24)
      : bucketsBetween(shiftStartH, shiftEndH);
    if (shiftBuckets.length === 0) return [];

    // Per-hour assigned count. Start at 0; add each officer's
    // contribution to the buckets they actually cover.
    const bucketCount = new Map<number, number>();
    for (const h of shiftBuckets) bucketCount.set(h, 0);
    const add = (buckets: number[], amount: number) => {
      for (const h of buckets) {
        if (bucketCount.has(h)) {
          bucketCount.set(h, (bucketCount.get(h) ?? 0) + amount);
        }
      }
    };
    for (const a of dayAssignments) {
      if (a.date !== date || a.shiftId !== shift.id) continue;
      if (a.status === "cancelled") continue;
      const note = (a.note ?? "").trim().toUpperCase();
      // Custom-hours note trumps everything else — the officer is
      // only present for those hours regardless of any code meaning.
      const hrs = parseHourNote(note);
      if (hrs) { add(bucketsBetween(hrs[0], hrs[1]), 1); continue; }
      if (!note) { add(shiftBuckets, 1); continue; }
      const meta = codeMap.get(note);
      if (meta) { add(shiftBuckets, meta.coverageValue); continue; }
      if (/^[A-Z]+$/.test(note)) continue; // letter-only custom = off
      // Explicit OT window (hours stored on the assignment row).
      const otS = hourOf(a.otStartTime);
      const otE = hourOf(a.otEndTime);
      if (otS !== null && otE !== null) {
        add(bucketsBetween(otS, otE), 1);
        continue;
      }
      add(shiftBuckets, 1);
    }
    // OT rows from OTHER-shift officers covering this shift.
    for (const a of dayAssignments) {
      if (a.date !== date || a.shiftId !== shift.id) continue;
      if (a.status === "cancelled") continue;
      const emp = projectedEmployees.find((e) => e.id === a.employeeId);
      if (!emp || emp.homeShiftId === shift.id) continue;
      const note = (a.note ?? "").trim().toUpperCase();
      const rawCov = note && codeMap.has(note) ? codeMap.get(note)!.coverageValue : 1;
      if (rawCov <= 0) continue;
      const otS = hourOf(a.otStartTime);
      const otE = hourOf(a.otEndTime);
      const otBuckets = (otS !== null && otE !== null)
        ? bucketsBetween(otS, otE)
        : shiftBuckets;
      add(otBuckets, rawCov);
    }

    // Walk the shift buckets in shift order and collect contiguous
    // runs where assigned < required. Contiguous = adjacent bucket
    // indices in shiftBuckets (not modular hours), so a 22-08 shift
    // still produces sane windows around midnight.
    const windows: Array<{ start: string; end: string }> = [];
    const fmt = (h: number) => `${String(h).padStart(2, "0")}:00`;
    let runStart: number | null = null;
    for (let i = 0; i < shiftBuckets.length; i++) {
      const h = shiftBuckets[i];
      const short = (bucketCount.get(h) ?? 0) < covRow.minStaffing;
      if (short && runStart === null) runStart = h;
      if (!short && runStart !== null) {
        windows.push({ start: fmt(runStart), end: fmt(h) });
        runStart = null;
      }
    }
    if (runStart !== null) {
      // Trailing run ends at the shift's own end.
      const endH = shiftBuckets[shiftBuckets.length - 1] + 1;
      windows.push({ start: fmt(runStart), end: fmt(endH % 24) });
    }
    return windows;
  }, [covRow, shift, date, dayAssignments, codeMap, projectedEmployees]);
  const awayToday = useMemo(() => {
    return homeRoster.flatMap((e) => {
      const a = dayAssignments.find(
        (x) => x.employeeId === e.id && x.shiftId === shift.id,
      );
      if (!a) return [];
      const note = (a.note ?? "").trim().toUpperCase();
      if (!note) return [];
      const cov = codeMap.get(note)?.coverageValue;
      // They count as "away" if their note reduces their contribution.
      if (cov !== undefined && cov < 1) return [{ emp: e, code: note }];
      return [];
    });
  }, [homeRoster, dayAssignments, shift.id, codeMap]);

  // Specialty shifts (inRotation=false) don't participate. Build a
  // set of shift IDs that are eligible to provide coverage. Use
  // the tac-filtered list so tac shifts don't count as a valid
  // home for a regular-mode supervisor (and vice versa).
  const rotationShiftIds = useMemo(
    () => new Set(
      visibleShifts
        .filter((s) => s.inRotation !== false && s.departmentId === shift.departmentId)
        .map((s) => s.id),
    ),
    [visibleShifts, shift.departmentId],
  );

  // Full-OT candidates: every active officer in the SAME department
  // who could plausibly take this shift, regardless of whether
  // their home is the gap shift itself. A supervisor often needs
  // to pull a same-shift officer in on their off-day (XXX, V, …)
  // to cover for someone else who called in — excluding them by
  // homeShiftId === shift.id hid Reyes (and anyone like him) from
  // the picker even though their cell was off for the day. Officers
  // on a non-rotation specialty home (Traffic, SRO, K-9) stay
  // excluded — those roles don't backfill patrol.
  const fullEligible = useMemo(() => {
    return projectedEmployees.filter(
      (e) => e.status !== "inactive"
        && e.departmentId === shift.departmentId
        && (e.homeShiftId == null || rotationShiftIds.has(e.homeShiftId)),
    );
  }, [projectedEmployees, shift.departmentId, rotationShiftIds]);

  // "Likely available" subset: eligible and not currently on a
  // working shift today (every assignment they have today must be
  // an off-code).
  const fullCandidates = useMemo(() => {
    return fullEligible.filter((e) => {
      const theirs = dayAssignments.filter((a) => a.employeeId === e.id);
      if (theirs.length === 0) return true;
      return theirs.every((a) => {
        const note = (a.note ?? "").trim().toUpperCase();
        if (!note) return false;
        const c = codeMap.get(note);
        return c !== undefined && c.coverageValue < 1;
      });
    });
  }, [fullEligible, dayAssignments, codeMap]);

  // Everyone else in the eligible pool — shown in a secondary
  // "All other employees" section so a supervisor can still offer
  // OT to someone who's currently working / would normally not be
  // considered available.
  const fullOthers = useMemo(() => {
    const avail = new Set(fullCandidates.map((e) => e.id));
    return fullEligible.filter((e) => !avail.has(e.id));
  }, [fullEligible, fullCandidates]);

  // Cross-department candidates: every active officer whose home
  // shift is in a *different* department from the gap. Useful when
  // a specialty unit (Dispatch, ACO, …) runs short and the agency
  // needs to pull a body from Patrol — same logic as Full OT but
  // without the same-department restriction. We still skip
  // employees who are already on a working shift today, then
  // expose the rest in a secondary "All other employees" list so
  // a supervisor can offer overtime to someone who's technically
  // scheduled too.
  const crossDeptEligible = useMemo(() => {
    return projectedEmployees.filter(
      (e) => e.status !== "inactive"
        && e.departmentId !== shift.departmentId
        && e.homeShiftId !== shift.id,
    );
  }, [employees.data, shift.id, shift.departmentId]);

  const crossDeptCandidates = useMemo(() => {
    return crossDeptEligible.filter((e) => {
      const theirs = dayAssignments.filter((a) => a.employeeId === e.id);
      if (theirs.length === 0) return true;
      return theirs.every((a) => {
        const note = (a.note ?? "").trim().toUpperCase();
        if (!note) return false;
        const c = codeMap.get(note);
        return c !== undefined && c.coverageValue < 1;
      });
    });
  }, [crossDeptEligible, dayAssignments, codeMap]);

  const crossDeptOthers = useMemo(() => {
    const avail = new Set(crossDeptCandidates.map((e) => e.id));
    return crossDeptEligible.filter((e) => !avail.has(e.id));
  }, [crossDeptEligible, crossDeptCandidates]);

  // Adjacent-shift candidates: employees whose home shift IS the
  // adjacent shift and who worked (or will work) the instance of
  // that shift that actually borders the gap.
  //
  // Critical for wrap-midnight shifts: the Night shift ending on
  // 4/25 morning was stored on 4/24, not 4/25. Checking 4/25 Nights
  // shows the NEXT night (starting 4/25 evening), which is a
  // different cohort of officers. We resolve the correct lookup
  // date via candidateAssignmentDate.
  function workingOn(candShift: Shift, role: "prev" | "next"): Employee[] {
    const lookupDate = candidateAssignmentDate(shift, candShift, date, role);
    return projectedEmployees.filter((e) => {
      if (e.status === "inactive") return false;
      if (e.homeShiftId !== candShift.id) return false;
      if (e.departmentId !== shift.departmentId) return false;
      const a = dayAssignments.find(
        (x) => x.employeeId === e.id && x.shiftId === candShift.id && x.date === lookupDate,
      );
      if (!a) return true;
      const note = (a.note ?? "").trim().toUpperCase();
      if (!note) return true;
      const c = codeMap.get(note);
      // Working if unknown (custom text) or coverage >= 1.
      return c === undefined || c.coverageValue >= 1;
    });
  }
  const prevCandidates = prev ? workingOn(prev, "prev") : [];
  const nextCandidates = next ? workingOn(next, "next") : [];

  const applyOicFilter = (list: Employee[]) =>
    oicOnly ? list.filter((e) => e.isOic) : list;
  const fullList = applyOicFilter(fullCandidates).sort(byName);
  const fullOthersList = applyOicFilter(fullOthers).sort(byName);
  const prevList = applyOicFilter(prevCandidates).sort(byName);
  const nextList = applyOicFilter(nextCandidates).sort(byName);
  const crossList = applyOicFilter(crossDeptCandidates).sort(byName);
  const crossOthersList = applyOicFilter(crossDeptOthers).sort(byName);

  // Lookup: employee → "Rank · Department" subtitle for the Cross-
  // dept picker rows. Lets the supervisor see at a glance who's
  // volunteering from where.
  const deptById = useMemo(
    () => new Map((departments.data ?? []).map((d) => [d.id, d])),
    [departments.data],
  );
  const crossSubtitleFor = (e: Employee) => {
    const dept = deptById.get(e.departmentId);
    const parts = [e.rank, dept?.name].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
  };

  const submitMut = useMutation({
    mutationFn: async () => {
      // Per-cell clear ahead of each OT insert. The assignments
      // table has a unique constraint on (employee_id, shift_id,
      // date) so an OT row can't coexist with the pattern's XXX
      // or any other existing assignment on the same cell. Drop
      // whatever's there first; the OT replaces it.
      const clearCell = (employeeId: string) =>
        api.clearCellAssignments({ employeeId, shiftId: shift.id, date });
      // A single coverage group id groups both halves of a split OT
      // (or marks a Full OT as its own group) so the Schedule can
      // render the pair as one "JJ-CS" chip on the consolidated OT
      // row instead of two separate rows.
      const coverageGroupId = crypto.randomUUID();
      if (tab === "full") {
        if (!fullPick) throw new Error("Pick an employee to cover the full shift.");
        if (!fullHours) throw new Error("Pick or enter the overtime hours.");
        await clearCell(fullPick);
        await api.createAssignment({
          employeeId: fullPick,
          shiftId: shift.id,
          date,
          note: "OT",
          overrideReason: "Overtime — full coverage",
          coverageGroupId,
          otStartTime: fullHours.start,
          otEndTime: fullHours.end,
        });
      } else if (tab === "cross") {
        if (!crossPick) throw new Error("Pick an employee from another department to cover this shift.");
        if (!crossHours) throw new Error("Pick or enter the overtime hours.");
        await clearCell(crossPick);
        await api.createAssignment({
          employeeId: crossPick,
          shiftId: shift.id,
          date,
          note: "OT",
          overrideReason: "Overtime — cross-department coverage",
          coverageGroupId,
          otStartTime: crossHours.start,
          otEndTime: crossHours.end,
        });
      } else {
        if (!prevPick || !nextPick) {
          throw new Error("Pick one employee from each adjacent shift.");
        }
        if (!prevHours || !nextHours) {
          throw new Error("Pick or enter overtime hours for each officer.");
        }
        // Both halves use note="OT"; the split is signalled by the
        // shared coverageGroupId. The schedule's OT row groups by
        // that id and renders the pair as "JJ-CS".
        await clearCell(prevPick);
        await api.createAssignment({
          employeeId: prevPick,
          shiftId: shift.id,
          date,
          note: "OT",
          overrideReason: "Overtime — covering first half",
          coverageGroupId,
          otStartTime: prevHours.start,
          otEndTime: prevHours.end,
        });
        await clearCell(nextPick);
        await api.createAssignment({
          employeeId: nextPick,
          shiftId: shift.id,
          date,
          note: "OT",
          overrideReason: "Overtime — covering second half",
          coverageGroupId,
          otStartTime: nextHours.start,
          otEndTime: nextHours.end,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      onClose();
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed to save OT coverage."),
  });

  // Supervisor override toggle. Mark the (shift, date) as covered
  // even when the bucket math reads short — used for staggered
  // start/stop shifts where the edge-hour gap is de-facto covered
  // by the adjacent shift overlap and a body isn't actually needed.
  const overrideMut = useMutation({
    mutationFn: async (reason: string | null) => {
      if (existingOverride) {
        await api.clearCoverageOverride({ shiftId: shift.id, date });
      } else {
        await api.setCoverageOverride({ shiftId: shift.id, date, reason });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["coverageOverrides"] });
      onClose();
    },
    onError: (e: { message?: string }) =>
      setError(e?.message ?? "Could not update override."),
  });

  const loading =
    employees.isLoading || shifts.isLoading || codes.isLoading
    || assignments.isLoading || coverageQ.isLoading
    || overridesQ.isLoading;

  return (
    <Modal
      open={true}
      onClose={() => { if (!submitMut.isPending) onClose(); }}
      title={`Fix coverage — ${shift.name}, ${date}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitMut.isPending || overrideMut.isPending}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => { setError(null); overrideMut.mutate(null); }}
            disabled={submitMut.isPending || overrideMut.isPending || loading}
            title={existingOverride
              ? "Drop the manual coverage override and reflect the real bucket count."
              : "Mark this cell covered even though the hour-bucket math reads short — used for staggered start/stop shifts where an edge-hour gap is fine."}
          >
            {overrideMut.isPending
              ? "Saving…"
              : existingOverride
                ? "Remove override"
                : "Mark covered (override)"}
          </Button>
          <Button
            onClick={() => { setError(null); submitMut.mutate(); }}
            disabled={
              submitMut.isPending
              || overrideMut.isPending
              || loading
              || (tab === "full"  && (!fullPick  || !fullHours))
              || (tab === "split" && (!prevPick  || !nextPick || !prevHours || !nextHours))
              || (tab === "cross" && (!crossPick || !crossHours))
            }
          >
            {submitMut.isPending
              ? "Saving…"
              : tab === "split" ? "Assign split OT"
              : tab === "cross" ? "Assign cross-dept OT"
              : "Assign OT"}
          </Button>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !covRow ? (
        <p className="text-sm text-slate-500">No coverage data for this shift / date.</p>
      ) : (
        <div className="space-y-3 text-sm">
          <CoverageSummary
            covRow={covRow}
            awayToday={awayToday}
            codeMap={codeMap}
          />

          {uncoveredWindows.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-900 text-xs">
              <span className="font-semibold">Uncovered:</span>{" "}
              {uncoveredWindows
                .map((w) => `${w.start}–${w.end}`)
                .join(", ")}
              . The OT hours picker below is pre-filled to the first
              gap — change it if the officer is only picking up part.
            </div>
          )}

          {oicOnly && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-xs">
              This gap is <strong>OIC-only</strong> — only OIC-qualified
              staff are shown below.
            </div>
          )}

          <div className="flex gap-1 border-b border-slate-200 flex-wrap">
            <TabButton active={tab === "full"} onClick={() => setTab("full")}>
              Full OT
            </TabButton>
            <TabButton active={tab === "split"} onClick={() => setTab("split")}>
              Split OT
            </TabButton>
            <TabButton active={tab === "cross"} onClick={() => setTab("cross")}>
              Cross-department
            </TabButton>
          </div>

          {tab === "full" && (
            <>
              <CandidatePicker
                label="Cover the full shift with"
                emptyHint="No eligible employees on other shifts today."
                candidates={fullList}
                otherCandidates={fullOthersList}
                pickedId={fullPick}
                onPick={(id) => {
                  setFullPick(id);
                  const emp = (employees.data ?? []).find((e) => e.id === id);
                  setFullHours(defaultHoursFor(emp, shifts.data ?? []));
                }}
              />
              {fullPick && (
                <HoursPicker
                  officer={(employees.data ?? []).find((e) => e.id === fullPick)}
                  shifts={shifts.data ?? []}
                  hours={fullHours}
                  onChange={setFullHours}
                />
              )}
            </>
          )}

          {tab === "split" && (!prev || !next
            ? (
              <p className="text-sm text-red-700">
                Split OT needs at least three shifts configured.
              </p>
            )
            : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <CandidatePicker
                    label={`From ${prev.name} (first half)`}
                    emptyHint={`No one working ${prev.name} today.`}
                    candidates={prevList}
                    pickedId={prevPick}
                    onPick={(id) => {
                      setPrevPick(id);
                      const emp = (employees.data ?? []).find((e) => e.id === id);
                      setPrevHours(defaultHoursFor(emp, shifts.data ?? []));
                    }}
                  />
                  <CandidatePicker
                    label={`From ${next.name} (second half)`}
                    emptyHint={`No one working ${next.name} today.`}
                    candidates={nextList}
                    pickedId={nextPick}
                    onPick={(id) => {
                      setNextPick(id);
                      const emp = (employees.data ?? []).find((e) => e.id === id);
                      setNextHours(defaultHoursFor(emp, shifts.data ?? []));
                    }}
                  />
                </div>
                {prevPick && (
                  <HoursPicker
                    officer={(employees.data ?? []).find((e) => e.id === prevPick)}
                    shifts={shifts.data ?? []}
                    hours={prevHours}
                    onChange={setPrevHours}
                  />
                )}
                {nextPick && (
                  <HoursPicker
                    officer={(employees.data ?? []).find((e) => e.id === nextPick)}
                    shifts={shifts.data ?? []}
                    hours={nextHours}
                    onChange={setNextHours}
                  />
                )}
              </div>
            )
          )}

          {tab === "cross" && (
            <>
              <p className="text-xs text-slate-500 -mt-1">
                Pull from any active officer in the agency — even
                ones whose home shift isn't part of this department's
                rotation. Useful when a specialty unit runs short.
              </p>
              <CandidatePicker
                label="Cover the full shift with"
                emptyHint="No eligible officers available today across other departments."
                candidates={crossList}
                otherCandidates={crossOthersList}
                pickedId={crossPick}
                onPick={(id) => {
                  setCrossPick(id);
                  const emp = (employees.data ?? []).find((e) => e.id === id);
                  setCrossHours(defaultHoursFor(emp, shifts.data ?? []));
                }}
                subtitleFor={crossSubtitleFor}
              />
              {crossPick && (
                <HoursPicker
                  officer={(employees.data ?? []).find((e) => e.id === crossPick)}
                  shifts={shifts.data ?? []}
                  hours={crossHours}
                  onChange={setCrossHours}
                />
              )}
            </>
          )}

          {error && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function byName(a: Employee, b: Employee) {
  return (a.lastName + a.firstName).localeCompare(b.lastName + b.firstName);
}

function CoverageSummary({
  covRow, awayToday, codeMap,
}: {
  covRow: { assigned: number; minStaffing: number; oicAssigned: number; oicRequired: number };
  awayToday: { emp: Employee; code: string }[];
  codeMap: Map<string, ShiftCode>;
}) {
  const totalShort = covRow.assigned < covRow.minStaffing;
  const oicShort = covRow.oicAssigned < covRow.oicRequired;
  return (
    <div>
      <div className="flex gap-4 font-medium">
        <span className={totalShort ? "text-red-700" : "text-emerald-700"}>
          {prettyNum(covRow.assigned)} of {covRow.minStaffing} assigned
        </span>
        <span className={oicShort ? "text-red-700" : "text-emerald-700"}>
          {prettyNum(covRow.oicAssigned)} of {covRow.oicRequired} OIC
        </span>
      </div>
      {awayToday.length > 0 && (
        <div className="mt-1.5 text-xs text-slate-500">
          Off today:{" "}
          {awayToday.map((a, i) => (
            <span key={a.emp.id}>
              {a.emp.lastName}
              <span className="text-slate-400"> · {codeMap.get(a.code)?.label ?? a.code}</span>
              {i < awayToday.length - 1 ? ", " : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function prettyNum(n: number) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1.5 text-sm border-b-2 -mb-px transition " +
        (active
          ? "border-brand-500 text-slate-900 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-700")
      }
    >
      {children}
    </button>
  );
}

function CandidatePicker({
  label, emptyHint, candidates, otherCandidates, pickedId, onPick,
  subtitleFor,
}: {
  label: string;
  emptyHint: string;
  candidates: Employee[];
  // Secondary list shown under an "All other employees" divider.
  // These are people who don't pass the normal "likely available"
  // heuristic (they're scheduled, on leave, etc.) but the
  // supervisor may still want to offer OT to — worth showing so
  // the picker doesn't feel artificially restrictive.
  otherCandidates?: Employee[];
  pickedId: string;
  onPick: (id: string) => void;
  // Override the per-row subtitle. Default subtitle is the
  // officer's rank. The Cross-department tab passes a function
  // that produces "Rank · Department" so the supervisor can tell
  // who's volunteering from where without leaving the modal.
  subtitleFor?: (e: Employee) => string | null;
}) {
  const renderRow = (e: Employee, dim: boolean) => {
    const active = pickedId === e.id;
    const subtitle = subtitleFor ? subtitleFor(e) : (e.rank ?? null);
    return (
      <button
        key={e.id}
        type="button"
        onClick={() => onPick(active ? "" : e.id)}
        className={
          "w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-slate-50 " +
          (active ? "bg-sky-50 hover:bg-sky-100 " : "") +
          (dim && !active ? "opacity-60" : "")
        }
      >
        <Avatar photo={e.photo} firstName={e.firstName} lastName={e.lastName} size={24} />
        <div className="min-w-0 flex-1">
          <div className="truncate">
            {e.lastName}, {e.firstName}
            {e.isOic && (
              <span className="ml-1.5 inline-block px-1 py-0 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200 align-middle">
                OIC
              </span>
            )}
          </div>
          {subtitle && (
            <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>
          )}
        </div>
        {active && <span className="text-sky-600">✓</span>}
      </button>
    );
  };
  const hasOthers = (otherCandidates?.length ?? 0) > 0;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </div>
      {candidates.length === 0 && !hasOthers ? (
        <p className="text-sm text-slate-500">{emptyHint}</p>
      ) : (
        <div className="border border-slate-200 rounded max-h-56 overflow-auto divide-y divide-slate-100">
          {candidates.map((e) => renderRow(e, false))}
          {hasOthers && (
            <div className="sticky top-0 z-10 bg-slate-100 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-t border-slate-200">
              All other employees
            </div>
          )}
          {hasOthers && otherCandidates!.map((e) => renderRow(e, true))}
        </div>
      )}
    </div>
  );
}

// Hours picker: for the currently-selected covering officer, show a
// compact preset chip set (from their home shift's overtime_windows)
// plus a Custom option that reveals start / end time inputs.
function HoursPicker({
  officer, shifts, hours, onChange,
}: {
  officer: Employee | undefined;
  shifts: Shift[];
  hours: OTHours | null;
  onChange: (h: OTHours | null) => void;
}) {
  if (!officer) return null;
  const home = shifts.find((s) => s.id === officer.homeShiftId);
  const presets: OvertimeWindow[] = home?.overtimeWindows ?? [];
  const selectedPreset = hours?.preset;
  const custom = !!hours && !selectedPreset;
  // A wrap-midnight range (end < start) is legal — B SET style OT
  // that runs 21:00-09:00 for example. Surface it here so the
  // supervisor sees the "→ next day" hint before saving, instead
  // of finding out from the schedule chip after the fact.
  const wraps = !!hours?.start && !!hours?.end && hours.end < hours.start;
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-xs">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center justify-between gap-2">
        <span>{officer.firstName} {officer.lastName} — OT hours</span>
        {wraps && (
          <span
            className="text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
            title="This range crosses midnight — the OT ends on the next calendar day."
          >
            wraps to next day
          </span>
        )}
      </div>
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {presets.map((p) => (
            <button
              key={p.label + p.startTime + p.endTime}
              type="button"
              onClick={() => onChange({ start: p.startTime, end: p.endTime, preset: p.label })}
              className={
                "rounded px-2 py-0.5 text-xs font-semibold border transition " +
                (selectedPreset === p.label
                  ? "bg-amber-100 text-amber-800 border-amber-300"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")
              }
              title={`${p.startTime} → ${p.endTime}`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onChange({ start: hours?.start ?? "06:00", end: hours?.end ?? "18:00" })}
            className={
              "rounded px-2 py-0.5 text-xs font-semibold border transition " +
              (custom
                ? "bg-amber-100 text-amber-800 border-amber-300"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")
            }
          >
            Custom
          </button>
        </div>
      )}
      {(presets.length === 0 || custom) && (
        <div className="flex items-center gap-2">
          <input
            type="time"
            value={hours?.start ?? ""}
            onChange={(e) =>
              onChange({ start: e.target.value, end: hours?.end ?? "" })
            }
            className="rounded border border-slate-300 px-1.5 py-0.5 text-xs"
          />
          <span className="text-slate-400">→</span>
          <input
            type="time"
            value={hours?.end ?? ""}
            onChange={(e) =>
              onChange({ start: hours?.start ?? "", end: e.target.value })
            }
            className="rounded border border-slate-300 px-1.5 py-0.5 text-xs"
          />
          {presets.length === 0 && (
            <span className="text-[11px] text-slate-500">
              (no presets configured on {home?.name ?? "this shift"})
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Shift a YYYY-MM-DD date by N days using local-time arithmetic so
// we don't drift across DST boundaries.
function shiftDate(ymd: string, offset: number): string {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() + offset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
